import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import useFetch from "../../../clients/features/get_withcredentials";
import {
  TbCalendar, TbList, TbTable,
  TbChevronLeft, TbChevronRight,
  TbSearch, TbChevronUp, TbChevronDown, TbSelector,
  TbCheck, TbX, TbAlertTriangle, TbEye, TbClock,
  TbArrowRight, TbArrowNarrowRight,
  TbUsers, TbWorld, TbBuildingStore
} from "react-icons/tb";

// ── Utils ─────────────────────────────────────────────────────────
const normalize = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const nights = (a, b) =>
  Math.ceil((new Date(b) - new Date(a)) / 86400000);

const fmtShort = (d) =>
  new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

const fmtLong = (d) =>
  new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

const MEAL_LABELS = {
  none: null,
  breakfast: "🍳 Petit-déjeuner",
  half_board: "🍽️ Demi-pension",
  full_board: "🍱 Pension complète"
};

// ── Couleurs par chambre ──────────────────────────────────────────
const ROOM_COLORS = [
  { bg: "bg-blue-100",    text: "text-blue-700",    bar: "bg-blue-400" },
  { bg: "bg-violet-100",  text: "text-violet-700",  bar: "bg-violet-400" },
  { bg: "bg-amber-100",   text: "text-amber-700",   bar: "bg-amber-400" },
  { bg: "bg-rose-100",    text: "text-rose-700",    bar: "bg-rose-400" },
  { bg: "bg-cyan-100",    text: "text-cyan-700",    bar: "bg-cyan-400" },
  { bg: "bg-emerald-100", text: "text-emerald-700", bar: "bg-emerald-400" },
];

const roomColor = (title = "") => {
  const i = title.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % ROOM_COLORS.length;
  return ROOM_COLORS[i];
};

// ── Status config ─────────────────────────────────────────────────
const STATUS = {
  confirmed:      { label: "Confirmée",          dot: "bg-emerald-400", badge: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  completed:      { label: "Terminée",            dot: "bg-slate-300",   badge: "bg-slate-50 text-slate-500 ring-slate-200" },
  pending:        { label: "En attente",          dot: "bg-amber-400",   badge: "bg-amber-50 text-amber-700 ring-amber-200" },
  pending_cancel: { label: "Annulation demandée", dot: "bg-orange-400",  badge: "bg-orange-50 text-orange-700 ring-orange-200" },
  cancelled:      { label: "Annulée",             dot: "bg-red-400",     badge: "bg-red-50 text-red-600 ring-red-200" },
  no_show:        { label: "No-show",             dot: "bg-slate-400",   badge: "bg-slate-100 text-slate-600 ring-slate-200" },
};

const getStatus = (s) => STATUS[s] || { label: s, dot: "bg-gray-300", badge: "bg-gray-100 text-gray-500 ring-gray-200" };

const StatusBadge = ({ status }) => {
  const s = getStatus(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${s.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// ── Helpers ───────────────────────────────────────────────────────
const formatGuests = (guests) => {
  if (!guests) return null;
  const parts = [];
  if (guests.adults > 0) parts.push(`${guests.adults} adulte${guests.adults > 1 ? "s" : ""}`);
  if (guests.children > 0) parts.push(`${guests.children} enfant${guests.children > 1 ? "s" : ""}`);
  return parts.join(" · ") || null;
};

// ── Modal détail ──────────────────────────────────────────────────
function ReservationModal({ r, onClose, onNoShow, onCancelApprove, cancelReq, loading }) {
  if (!r) return null;
  const today = normalize(new Date());
  const ci = normalize(r.checkInDate);
  const co = normalize(r.checkOutDate);
  const isPast     = today >= co;
  const isOngoing  = today >= ci && today < co;
  const isUpcoming = today < ci;
  const n = nights(r.checkInDate, r.checkOutDate);
  const meal = MEAL_LABELS[r.ratePlanSnapshot?.mealPlan];
  const guestsLabel = formatGuests(r.guests);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Accent bar */}
        <div className="h-0.5 w-full bg-gradient-to-r from-[#3A9D23] via-[#5abf3a] to-[#3A9D23]/30" />

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-lg">
                {r.reservationId}
              </span>
              {r.source === "admin" ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-purple-50 text-purple-600 text-[11px] font-semibold">
                  <TbBuildingStore className="text-xs" /> Admin
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-blue-50 text-blue-600 text-[11px] font-semibold">
                  <TbWorld className="text-xs" /> Site web
                </span>
              )}
            </div>
            <h3 className="text-lg font-bold text-gray-900">{r.guest?.firstName} {r.guest?.lastName}</h3>
            <div className="mt-1.5"><StatusBadge status={r.status} /></div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 transition text-gray-400">
            <TbX />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto max-h-[60vh]">

          {/* Dates highlight */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl mb-5">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{new Date(r.checkInDate).getDate()}</p>
              <p className="text-xs text-gray-400">{new Date(r.checkInDate).toLocaleString("fr-FR", { month: "short" })}</p>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <div className="flex-1 h-px bg-gray-300" />
              <span className="text-xs text-gray-400 shrink-0">{n} nuit{n > 1 ? "s" : ""}</span>
              <div className="flex-1 h-px bg-gray-300" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{new Date(r.checkOutDate).getDate()}</p>
              <p className="text-xs text-gray-400">{new Date(r.checkOutDate).toLocaleString("fr-FR", { month: "short" })}</p>
            </div>
          </div>

          {/* Infos */}
          <div className="flex flex-col gap-0">
            {[
              { label: "Chambre",    value: r.roomId?.title || "—" },
              { label: "Tarif",      value: r.ratePlanSnapshot?.name || "Standard" },
              ...(meal ? [{ label: "Repas", value: meal }] : []),
              ...(guestsLabel ? [{ label: "Voyageurs", value: guestsLabel }] : []),
              { label: "Email",      value: r.guest?.email || "—" },
              { label: "Tél",        value: r.guest?.phone || "—" },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                <span className="text-sm text-gray-400">{item.label}</span>
                <span className="text-sm font-medium text-gray-800 text-right max-w-[60%] truncate">{item.value}</span>
              </div>
            ))}
          </div>

          {/* Prix */}
          <div className="flex items-end justify-between pt-4 mt-2 border-t border-gray-100">
            <span className="text-sm text-gray-500">Total</span>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{r.totalPrice?.toLocaleString("fr-FR")} €</p>
              {r.totalPriceKMF && (
                <p className="text-xs text-gray-400">{r.totalPriceKMF?.toLocaleString("fr-FR")} KMF</p>
              )}
            </div>
          </div>

          {isOngoing && (
            <div className="mt-4 px-4 py-2.5 bg-emerald-50 rounded-xl text-sm text-emerald-700 font-medium flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Séjour en cours
            </div>
          )}
          {isUpcoming && (
            <div className="mt-4 px-4 py-2.5 bg-blue-50 rounded-xl text-sm text-blue-600 font-medium flex items-center gap-2">
              <TbClock /> Arrivée le {fmtLong(r.checkInDate)}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-5 flex flex-col gap-2 border-t border-gray-50 pt-4">
          {r.status === "confirmed" && isPast && (
            <button
              onClick={() => onNoShow(r._id)}
              disabled={loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition disabled:opacity-50"
            >
              {loading ? "..." : "Marquer No-show"}
            </button>
          )}
          {r.status === "pending_cancel" && cancelReq && (
            <button
              onClick={() => onCancelApprove(cancelReq._id)}
              disabled={loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <TbCheck /> {loading ? "..." : "Confirmer l'annulation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────
export default function Reservations() {
  const { hotelId } = useParams();
  const [view, setView] = useState("table");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelRequests, setCancelRequests] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("checkInDate");
  const [sortDir, setSortDir] = useState("desc");
  const [hotelStats, setHotelStats] = useState(null);
  const [listFilter, setListFilter] = useState("all");

  const { data = [], loading: dataLoading, refreshData } = useFetch(
    `https://api.komorotel.com/api/reservation/hotel/${hotelId}`
  );

  useEffect(() => {
    const m = currentDate.getMonth();
    const y = currentDate.getFullYear();
    axios.get(
      `https://api.komorotel.com/api/reservation/hotel/${hotelId}/stats?month=${m}&year=${y}`,
      { withCredentials: true }
    ).then(r => setHotelStats(r.data)).catch(console.error);
  }, [hotelId, currentDate]);

  useEffect(() => {
    axios.get(
      `https://api.komorotel.com/api/reservation/hotel/${hotelId}/pending-cancellations`,
      { withCredentials: true }
    ).then(r => setCancelRequests(r.data)).catch(console.error);
  }, [hotelId]);

  // ── Actions ───────────────────────────────────────────────────
  const handleNoShow = async (id) => {
    try {
      setLoading(true);
      await axios.put(`https://api.komorotel.com/api/reservation/no-show/${id}`, {}, { withCredentials: true });
      setSelected(null); refreshData();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCancelApprove = async (reqId) => {
    try {
      setLoading(true);
      await axios.put(
        `https://api.komorotel.com/api/reservation/cancellation-requests/${reqId}`,
        { status: "approved" }, { withCredentials: true }
      );
      setSelected(null); refreshData();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // ── Stats ─────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const today = normalize(new Date());
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const monthData = data.filter(r => {
      const s = normalize(r.checkInDate);
      const e = normalize(r.checkOutDate);
      return s <= new Date(year, month + 1, 0) && e >= new Date(year, month, 1);
    });
    return {
      monthTotal:     monthData.length,
      confirmed:      data.filter(r => r.status === "confirmed" && normalize(r.checkOutDate) >= today).length,
      completed:      data.filter(r => r.status === "completed").length,
      pending_cancel: data.filter(r => r.status === "pending_cancel").length,
      cancelled:      data.filter(r => r.status === "cancelled").length,
      no_show:        data.filter(r => r.status === "no_show").length,
    };
  }, [data, currentDate]);

  // ── Calendrier ────────────────────────────────────────────────
  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
  const monthName = currentDate.toLocaleString("fr-FR", { month: "long", year: "numeric" });

  const monthReservations = useMemo(() => data.filter(r => {
    const s = normalize(r.checkInDate);
    const e = normalize(r.checkOutDate);
    return s <= new Date(year, month + 1, 0) && e >= new Date(year, month, 1);
  }), [data, year, month]);

  // ✅ Exclure cancelled et pending_cancel du calendrier
  const getDayRes = (date) => {
    const d = normalize(date);
    return monthReservations.filter(r =>
      d >= normalize(r.checkInDate) &&
      d < normalize(r.checkOutDate) &&
      !["cancelled", "pending_cancel"].includes(r.status)
    );
  };

  const uniqueRooms = useMemo(() => {
    const m = new Map();
    monthReservations
      .filter(r => !["cancelled", "pending_cancel"].includes(r.status))
      .forEach(r => { if (r.roomId?.title) m.set(r.roomId.title, true); });
    return Array.from(m.keys());
  }, [monthReservations]);

  // ── Table ─────────────────────────────────────────────────────
  const toggleSort = (f) => {
    if (sortField === f) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(f); setSortDir("asc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <TbSelector className="text-gray-300 ml-1 inline" />;
    return sortDir === "asc"
      ? <TbChevronUp className="text-gray-500 ml-1 inline" />
      : <TbChevronDown className="text-gray-500 ml-1 inline" />;
  };

  const tableData = useMemo(() => {
    const today = normalize(new Date());
    let filtered = data.filter(r => {
      const text = search.toLowerCase();
      const matchSearch =
        r.guest?.firstName?.toLowerCase().includes(text) ||
        r.guest?.lastName?.toLowerCase().includes(text) ||
        r.roomId?.title?.toLowerCase().includes(text) ||
        r.reservationId?.toLowerCase().includes(text);

      let matchStatus = true;
      if (statusFilter === "confirmed")
        matchStatus = r.status === "confirmed" && normalize(r.checkOutDate) >= today;
      else if (statusFilter === "cancelled")
        // ✅ "Annulées" regroupe cancelled + pending_cancel
        matchStatus = ["cancelled", "pending_cancel"].includes(r.status);
      else if (statusFilter !== "all")
        matchStatus = r.status === statusFilter;

      return matchSearch && matchStatus;
    });

    filtered.sort((a, b) => {
      // ✅ pending_cancel toujours en premier
      if (a.status === "pending_cancel" && b.status !== "pending_cancel") return -1;
      if (b.status === "pending_cancel" && a.status !== "pending_cancel") return 1;

      let vA, vB;
      if (sortField === "guest")       { vA = a.guest?.lastName||""; vB = b.guest?.lastName||""; }
      if (sortField === "checkInDate") { vA = new Date(a.checkInDate); vB = new Date(b.checkInDate); }
      if (sortField === "price")       { vA = a.totalPrice||0; vB = b.totalPrice||0; }
      if (sortField === "room")        { vA = a.roomId?.title||""; vB = b.roomId?.title||""; }
      if (!vA && !vB) return 0;
      return vA < vB ? (sortDir==="asc"?-1:1) : vA > vB ? (sortDir==="asc"?1:-1) : 0;
    });

    return filtered;
  }, [data, search, statusFilter, sortField, sortDir]);

  // ✅ Liste — pending_cancel en premier, puis par date
  const listData = useMemo(() => {
    const filtered = listFilter === "all"
      ? data
      : listFilter === "cancelled"
        ? data.filter(r => ["cancelled", "pending_cancel"].includes(r.status))
        : data.filter(r => r.status === listFilter);

    return filtered.sort((a, b) => {
      if (a.status === "pending_cancel" && b.status !== "pending_cancel") return -1;
      if (b.status === "pending_cancel" && a.status !== "pending_cancel") return 1;
      return new Date(b.checkInDate) - new Date(a.checkInDate);
    });
  }, [data, listFilter]);

  // ✅ Filtres — "Annulées" regroupe cancelled + pending_cancel
  const tableFilters = [
    { key: "all",       label: "Toutes",    count: data.length },
    { key: "confirmed", label: "En cours",  count: stats.confirmed },
    { key: "completed", label: "Terminées", count: stats.completed },
    { key: "cancelled", label: "Annulées",  count: stats.cancelled + stats.pending_cancel },
    { key: "no_show",   label: "No-show",   count: stats.no_show },
  ];

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#3A9D23] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ───────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Réservations</h1>
          <p className="text-sm text-gray-400 mt-1">Calendrier, liste et gestion des séjours</p>
        </div>
        <div className="text-right bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm">
          <p className="text-xs text-gray-400 mb-1">Revenus bruts</p>
          <p className="text-xl font-bold text-gray-900">
            {hotelStats ? hotelStats.revenuBrut.toLocaleString("fr-FR") : "…"} €
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Commission due : <span className="text-orange-500 font-semibold">
              {hotelStats ? hotelStats.commissionDue.toLocaleString("fr-FR") : "…"} €
            </span>
          </p>
        </div>
      </div>

      {/* Toggle vue */}
      <div className="flex items-center gap-1 bg-white border border-gray-100 shadow-sm p-1 rounded-xl w-fit">
        {[
          { key: "table",    icon: <TbTable />,    label: "Tableau" },
          { key: "list",     icon: <TbList />,     label: "Liste" },
          { key: "calendar", icon: <TbCalendar />, label: "Calendrier" },
        ].map(v => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
              view === v.key ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {v.icon} {v.label}
          </button>
        ))}
      </div>

      {/* ── ALERTE ANNULATIONS — toujours visible si pending_cancel ── */}
      {stats.pending_cancel > 0 && (
        <div className="px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl flex items-center gap-3">
          <TbAlertTriangle className="text-orange-500 text-lg shrink-0" />
          <p className="text-sm text-orange-700 font-medium">
            {stats.pending_cancel} demande{stats.pending_cancel > 1 ? "s" : ""} d'annulation en attente de traitement
          </p>
          <button
            onClick={() => { setView("table"); setStatusFilter("cancelled"); }}
            className="ml-auto text-xs font-semibold text-orange-600 hover:text-orange-800 flex items-center gap-1 shrink-0"
          >
            Traiter <TbArrowRight />
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          VUE CALENDRIER
      ══════════════════════════════════════════════════════ */}
      {view === "calendar" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Ce mois",      value: stats.monthTotal,    color: "text-gray-900" },
            { label: "En cours",     value: stats.confirmed,     color: "text-emerald-600" },
            { label: "Terminées",    value: stats.completed,     color: "text-slate-500" },
            {
              label: "Revenus mois",
              value: hotelStats ? `${hotelStats.monthRevenuBrut.toLocaleString("fr-FR")} €` : "…",
              sub: hotelStats ? `Commission : ${hotelStats.monthCommissionDue.toLocaleString("fr-FR")} €` : "",
              color: "text-[#3A9D23]"
            },
          ].map((k, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
              <p className="text-xs text-gray-400 mb-1">{k.label}</p>
              <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
              {k.sub && <p className="text-xs text-orange-500 font-medium mt-1">{k.sub}</p>}
            </div>
          ))}
        </div>
      )}

      {view === "calendar" && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition"
              >
                <TbChevronLeft className="text-gray-600" />
              </button>
              <h2 className="text-base font-bold text-gray-900 min-w-[160px] text-center capitalize">
                {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
              </h2>
              <button
                onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition"
              >
                <TbChevronRight className="text-gray-600" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {uniqueRooms.map(title => {
                const c = roomColor(title);
                return (
                  <span key={title} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.bar}`} /> {title}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-7 border-b border-gray-100">
              {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d => (
                <div key={d} className="py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: offset }).map((_, i) => (
                <div key={`e-${i}`} className="min-h-[110px] border-b border-r border-gray-50 bg-gray-50/30" />
              ))}
              {days.map((day, i) => {
                const isToday = normalize(day).getTime() === normalize(new Date()).getTime();
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const dayRes = getDayRes(day);
                const isLastCol = (offset + i + 1) % 7 === 0;
                const isPast = normalize(day) < normalize(new Date());
                return (
                  <div
                    key={i}
                    className={`min-h-[110px] p-2 border-b border-r border-gray-50 transition-colors
                      ${isLastCol ? "border-r-0" : ""}
                      ${isWeekend ? "bg-gray-50/40" : "bg-white"}
                      ${isPast && !isToday ? "opacity-60" : ""}
                    `}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold mb-1.5
                      ${isToday ? "bg-[#3A9D23] text-white" : "text-gray-600"}
                    `}>
                      {day.getDate()}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {dayRes.slice(0, 3).map((r, ri) => {
                        const c = roomColor(r.roomId?.title);
                        const isStart = normalize(day).getTime() === normalize(r.checkInDate).getTime();
                        const isEnd = normalize(day).getTime() === normalize(new Date(new Date(r.checkOutDate).getTime() - 86400000)).getTime();
                        return (
                          <div
                            key={ri}
                            onClick={() => setSelected(r)}
                            className={`px-1.5 py-0.5 text-xs font-medium cursor-pointer truncate transition
                              ${c.bg} ${c.text}
                              ${isStart ? "rounded-l-md" : ""}
                              ${isEnd ? "rounded-r-md" : ""}
                              hover:opacity-75
                            `}
                          >
                            {isStart ? `${r.roomId?.title || "Chambre"} · ${r.guest?.firstName || ""}` : <span className="opacity-0">·</span>}
                          </div>
                        );
                      })}
                      {dayRes.length > 3 && (
                        <p className="text-xs text-gray-400 pl-1 mt-0.5">+{dayRes.length - 3}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          VUE LISTE
      ══════════════════════════════════════════════════════ */}
      {view === "list" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {[
              { key: "all",       label: "Toutes",    count: data.length },
              { key: "confirmed", label: "Confirmées", count: stats.confirmed },
              { key: "completed", label: "Terminées",  count: stats.completed },
              { key: "cancelled", label: "Annulées",   count: stats.cancelled + stats.pending_cancel },
              { key: "no_show",   label: "No-show",    count: stats.no_show },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setListFilter(f.key)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
                  listFilter === f.key ? "bg-gray-900 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                {f.label} <span className="opacity-50 ml-0.5">({f.count})</span>
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            {listData.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400 text-sm">
                Aucune réservation
              </div>
            )}
            {listData.map(r => {
              const n = nights(r.checkInDate, r.checkOutDate);
              const today = normalize(new Date());
              const isOngoing  = today >= normalize(r.checkInDate) && today < normalize(r.checkOutDate);
              const isUpcoming = today < normalize(r.checkInDate);
              const c = roomColor(r.roomId?.title);
              const guestsLabel = formatGuests(r.guests);

              return (
                <div
                  key={r._id}
                  onClick={() => setSelected(r)}
                  className={`bg-white rounded-2xl border p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all
                    ${r.status === "pending_cancel" ? "border-orange-200 bg-orange-50/30" : "border-gray-100"}
                  `}
                >
                  <div className={`w-1 self-stretch rounded-full ${r.status === "pending_cancel" ? "bg-orange-400" : c.bar}`} />

                  <div className="text-center min-w-[52px]">
                    <p className="text-lg font-bold text-gray-900">{new Date(r.checkInDate).getDate()}</p>
                    <p className="text-xs text-gray-400">{new Date(r.checkInDate).toLocaleString("fr-FR", { month: "short" })}</p>
                  </div>
                  <TbArrowNarrowRight className="text-gray-300 shrink-0" />
                  <div className="text-center min-w-[52px]">
                    <p className="text-lg font-bold text-gray-900">{new Date(r.checkOutDate).getDate()}</p>
                    <p className="text-xs text-gray-400">{new Date(r.checkOutDate).toLocaleString("fr-FR", { month: "short" })}</p>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{r.guest?.firstName} {r.guest?.lastName}</p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {r.roomId?.title} · {n} nuit{n > 1 ? "s" : ""}
                      {guestsLabel && <span className="ml-1">· {guestsLabel}</span>}
                    </p>
                  </div>

                  {isOngoing  && <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 font-medium shrink-0">En cours</span>}
                  {isUpcoming && r.status !== "pending_cancel" && <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600 font-medium shrink-0">À venir</span>}
                  {r.status === "pending_cancel" && (
                    <span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-orange-600 font-semibold shrink-0 flex items-center gap-1">
                      <TbAlertTriangle className="text-xs" /> À traiter
                    </span>
                  )}

                  <StatusBadge status={r.status} />

                  <div className="text-right shrink-0">
                    <p className="font-bold text-gray-900">{r.totalPrice?.toLocaleString("fr-FR")} €</p>
                    {r.totalPriceKMF && (
                      <p className="text-[10px] text-gray-400">{r.totalPriceKMF?.toLocaleString("fr-FR")} KMF</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          VUE TABLEAU
      ══════════════════════════════════════════════════════ */}
      {view === "table" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1.5">
              {tableFilters.map(f => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                    statusFilter === f.key ? "bg-gray-900 text-white shadow-sm" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {f.label} <span className="ml-1 opacity-50">({f.count})</span>
                </button>
              ))}
            </div>
            <div className="relative">
              <TbSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Client, chambre, référence..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition bg-white w-64"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    {[
                      { label: "Référence", field: null },
                      { label: "Client",    field: "guest" },
                      { label: "Dates",     field: "checkInDate" },
                      { label: "Nuits",     field: null },
                      { label: "Chambre",   field: "room" },
                      { label: "Voyageurs", field: null },
                      { label: "Total",     field: "price" },
                      { label: "Statut",    field: null },
                      { label: "",          field: null },
                    ].map((col, i) => (
                      <th
                        key={i}
                        onClick={() => col.field && toggleSort(col.field)}
                        className={`text-left px-4 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wider ${col.field ? "cursor-pointer hover:text-gray-700 select-none" : ""}`}
                      >
                        {col.label}{col.field && <SortIcon field={col.field} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-16 text-gray-400 text-sm">Aucune réservation trouvée</td></tr>
                  )}
                  {tableData.map(r => {
                    const today = normalize(new Date());
                    const isUpcoming = today < normalize(r.checkInDate);
                    const isOngoing  = today >= normalize(r.checkInDate) && today < normalize(r.checkOutDate);
                    const isPast     = today >= normalize(r.checkOutDate);
                    const cancelReq  = cancelRequests?.find(cr => String(cr.reservationId?._id || cr.reservationId) === String(r._id));
                    const n = nights(r.checkInDate, r.checkOutDate);
                    const guestsLabel = formatGuests(r.guests);
                    const isPendingCancel = r.status === "pending_cancel";

                    return (
                      <tr
                        key={r._id}
                        onClick={() => setSelected(r)}
                        className={`border-b border-gray-50 last:border-0 cursor-pointer transition-colors
                          ${isPendingCancel ? "bg-orange-50/40 hover:bg-orange-50/60" : r.status === "cancelled" ? "opacity-50 hover:bg-gray-50/70" : "hover:bg-gray-50/70"}
                        `}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
                              {r.reservationId || r._id.slice(0, 8)}
                            </span>
                            {r.source === "admin" && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-purple-50 text-purple-500 font-semibold">Admin</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-semibold text-gray-900">{r.guest?.firstName} {r.guest?.lastName}</p>
                          {r.guest?.email && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[160px]">{r.guest.email}</p>}
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-gray-800 font-medium">{fmtShort(r.checkInDate)} → {fmtShort(r.checkOutDate)}</p>
                          {["confirmed","completed"].includes(r.status) && (
                            <p className={`text-xs mt-0.5 font-medium ${isUpcoming ? "text-blue-400" : isOngoing ? "text-emerald-500" : "text-gray-400"}`}>
                              {isUpcoming && "À venir"}{isOngoing && "● En cours"}{isPast && "Terminé"}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-4 text-gray-500 text-sm">{n}n</td>
                        <td className="px-4 py-4 text-gray-700 font-medium">{r.roomId?.title || "—"}</td>
                        <td className="px-4 py-4">
                          {guestsLabel ? (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                              <TbUsers className="shrink-0" /> {guestsLabel}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-bold text-gray-900">{r.totalPrice?.toLocaleString("fr-FR")} €</p>
                          {r.totalPriceKMF && (
                            <p className="text-[10px] text-gray-400">{r.totalPriceKMF?.toLocaleString("fr-FR")} KMF</p>
                          )}
                        </td>
                        <td className="px-4 py-4"><StatusBadge status={r.status} /></td>
                        <td className="px-4 py-4">
                          {isPendingCancel && cancelReq && (
                            <span className="text-xs font-semibold text-orange-500 flex items-center gap-1">
                              <TbAlertTriangle /> À traiter
                            </span>
                          )}
                          {r.status === "confirmed" && isPast && (
                            <span className="text-xs text-gray-400">No-show ?</span>
                          )}
                          {!["pending_cancel","cancelled","no_show"].includes(r.status) && !isPast && (
                            <TbEye className="text-gray-300 hover:text-gray-500 transition" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {tableData.length > 0 && (
              <div className="px-5 py-3 border-t border-gray-50">
                <p className="text-xs text-gray-400">{tableData.length} résultat{tableData.length > 1 ? "s" : ""}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL ────────────────────────────────────────────── */}
      {selected && (
        <ReservationModal
          r={selected}
          onClose={() => setSelected(null)}
          onNoShow={handleNoShow}
          onCancelApprove={handleCancelApprove}
          cancelReq={cancelRequests?.find(cr => String(cr.reservationId?._id || cr.reservationId) === String(selected._id))}
          loading={loading}
        />
      )}
    </div>
  );
}